import { countConditions } from "@estelle/domain";
import type {
  ChannelWrite,
  ListQuery,
  Page,
  SyncEvent,
  SyncEventsQuery,
  SyncRunResult,
  SyncStatus,
  WriteResult,
} from "@estelle/domain";

import { apiFetch } from "@/adapters/api/base";

/**
 * The Shopify sync status page, over HTTP (#154, FR-SYN-7).
 *
 * `apps/api/src/routes/sync.ts` is the other half of this file — `GET
 * /status` for the sweep cards and webhook health, `GET /events` for the
 * recent-deliveries list, `POST /run` for the "Sync now" button. None of the
 * three ever returns a webhook payload; there is not one to return.
 */

const BASE = "/api/sync";

function params(query: SyncEventsQuery): URLSearchParams {
  const out = new URLSearchParams();
  if (query.status) out.set("status", query.status);
  if (query.topic) out.set("topic", query.topic);
  if (query.limit !== undefined) out.set("limit", String(query.limit));
  if (query.cursor !== undefined) out.set("cursor", query.cursor);
  return out;
}

/** The shared list query, the way every other `/api/*` list reads it. */
function listParams(query: ListQuery | undefined): URLSearchParams {
  const out = new URLSearchParams();
  if (!query) return out;
  if (query.limit !== undefined) out.set("limit", String(query.limit));
  if (query.cursor !== undefined) out.set("cursor", query.cursor);
  if (query.filter && countConditions(query.filter) > 0) {
    out.set("filter", JSON.stringify(query.filter));
  }
  if (query.sort && query.sort.length > 0) {
    out.set(
      "sort",
      query.sort.map((entry) => `${entry.column}:${entry.direction}`).join(","),
    );
  }
  return out;
}

export function createApiSync() {
  return {
    /**
     * Every write the ERP has attempted at Shopify (#164, #173) — the rows
     * `writeThrough()` records, behind `settings.sync.view`. Status only in
     * a thrown error: a write's request can carry an order note.
     */
    listChannelWrites: async (
      query: ListQuery,
    ): Promise<Page<ChannelWrite>> => {
      const search = listParams(query).toString();
      const response = await apiFetch(
        `${BASE}/writes${search ? `?${search}` : ""}`,
      );
      if (!response.ok) {
        throw new Error(
          `Channel writes request failed: ${String(response.status)}`,
        );
      }
      return (await response.json()) as Page<ChannelWrite>;
    },

    getChannelWrite: async (id: string): Promise<ChannelWrite | null> => {
      const response = await apiFetch(
        `${BASE}/writes/${encodeURIComponent(id)}`,
      );
      if (!response.ok) {
        throw new Error(
          `Channel write request failed: ${String(response.status)}`,
        );
      }
      return (await response.json()) as ChannelWrite | null;
    },

    getSyncStatus: async (): Promise<SyncStatus> => {
      const response = await apiFetch(`${BASE}/status`);
      if (!response.ok) {
        throw new Error(
          `Sync status request failed: ${String(response.status)}`,
        );
      }
      return (await response.json()) as SyncStatus;
    },

    listSyncEvents: async (
      query: SyncEventsQuery,
    ): Promise<Page<SyncEvent>> => {
      const search = params(query).toString();
      const response = await apiFetch(
        `${BASE}/events${search ? `?${search}` : ""}`,
      );
      if (!response.ok) {
        throw new Error(
          `Sync events request failed: ${String(response.status)}`,
        );
      }
      return (await response.json()) as Page<SyncEvent>;
    },

    /**
     * A 503 means Shopify credentials are not configured — a precondition
     * the screen shows in place, not a transport failure, so it comes back
     * as a `blocked` rejection rather than a thrown error. A 202 (including
     * the ordinary "joined a sweep already running" case) is `ok: true`;
     * `alreadyRunning` is what tells those two apart on screen.
     */
    runSync: async (): Promise<WriteResult<SyncRunResult>> => {
      const response = await apiFetch(`${BASE}/run`, { method: "POST" });
      const body = (await response.json().catch(() => null)) as
        SyncRunResult | { error: string } | null;

      if (response.status === 503 && body && "error" in body) {
        return {
          ok: false,
          rejection: { kind: "blocked", message: body.error },
        };
      }
      if (response.ok && body && "started" in body) {
        return { ok: true, data: body };
      }
      throw new Error(`Sync run failed: ${String(response.status)}`);
    },
  };
}
